import cloneDeep from 'lodash/cloneDeep';
import without from 'lodash/without';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FloweringDatesNames, FLOWERING_DATES } from '../../../../../res/Constants';
import { canEditGrowing } from '../../../../../service/PermissionService';
import { updateTechFormCharacters } from '../../../../../service/TechFormDetailService';
import { toastWarning } from '../../../../../ui/ToastHelper';
import { getHarvestLines } from '../../../../../utils/Utils';
import { A, B, HTTP_CODE } from './../../../../../res/Constants';
import GreenRedTimeLine from './../components/GreenRedTimeLine';

const getSelectedLine = (line) => (line === A ? 'lineA' : 'lineB');

const floweringKeys = Object.keys(FloweringDatesNames);

// Fields that support multiple dates
const MULTI_DATE_FIELDS = ['pinching', 'harvesting'];

/**
 * Parse multiple dates from techFormData
 * The API now returns dates in array format with "Dates" suffix:
 * - pinchingDatesA: [1766444400000, ...]
 * - harvestingDatesA: [1766444400000, ...]
 */
const parseMultipleDates = (techFormData, keyName, line) => {
    // Use the NEW field name with "Dates" suffix (e.g., "pinchingDatesA")
    const fieldName = `${keyName}Dates${line}`;
    const value = techFormData[fieldName];

    if (!value) {
        return [];
    }

    // The API returns an array of timestamps
    if (Array.isArray(value)) {
        return value.filter(d => d != null && !isNaN(d));
    }

    // If for some reason it's not an array, return empty
    return [];
};

const calculateDates = (techFormData, line) => {
    return floweringKeys.map((keyName) => {
        if (MULTI_DATE_FIELDS.includes(keyName)) {
            // Handle multiple dates for pinching and harvesting
            const dates = parseMultipleDates(techFormData, keyName, line);
            return {
                id: keyName,
                name: FloweringDatesNames[keyName],
                dates: dates.length > 0 ? dates : [], // Array of dates
                isMultiDate: true
            };
        } else {
            // Single date for other fields
            const techField = `${keyName}${line}`;
            return {
                id: keyName,
                name: FloweringDatesNames[keyName],
                date: techFormData[techField],
                isMultiDate: false
            };
        }
    });
};

const extractLines = (techFormData) => {
    return {
        lineA: {
            title: FLOWERING_DATES,
            dates: calculateDates(techFormData, A)
        },
        lineB: {
            title: FLOWERING_DATES,
            dates: calculateDates(techFormData, B)
        }
    };
};

export default class FloweringDates extends PureComponent {
    static propTypes = {
        cacType: PropTypes.number.isRequired,
        techFormData: PropTypes.object.isRequired
    };

    state = {
        lines: extractLines(this.props.techFormData),
        pendingFieldIds: []
    };

    onDateEdited = (line, id, dateOrIndex, newDate) => {
        const selectedLine = getSelectedLine(line);
        this.setState((state) => {
            const newLines = cloneDeep(state.lines);
            newLines[selectedLine].dates = newLines[selectedLine].dates.map((obj) => {
                if (obj.id !== id) return obj;

                if (obj.isMultiDate) {
                    // Handle multi-date fields (dateOrIndex is the array index, newDate is the actual date)
                    const dateIndex = dateOrIndex;
                    const updatedDates = [...(obj.dates || [])];
                    updatedDates[dateIndex] = newDate;
                    return {
                        ...obj,
                        techFormPk: this.props.techFormData.techFormPk,
                        dates: updatedDates,
                        line
                    };
                } else {
                    // Handle single-date fields (dateOrIndex is the actual date)
                    return {
                        ...obj,
                        techFormPk: this.props.techFormData.techFormPk,
                        date: dateOrIndex,
                        line
                    };
                }
            });
            return {
                lines: newLines,
                pendingFieldIds: [...state.pendingFieldIds, id]
            };
        });
    };

    onAddDate = (line, id) => {
        const selectedLine = getSelectedLine(line);
        this.setState((state) => {
            const newLines = cloneDeep(state.lines);
            newLines[selectedLine].dates = newLines[selectedLine].dates.map((obj) => {
                if (obj.id !== id || !obj.isMultiDate) return obj;

                // Filter out null dates before adding a new one
                const validDates = (obj.dates || []).filter(d => d != null);

                return {
                    ...obj,
                    techFormPk: this.props.techFormData.techFormPk,
                    dates: [...validDates, null], // Add empty date slot after cleaning
                    line
                };
            });
            return {
                lines: newLines,
                pendingFieldIds: [...state.pendingFieldIds, id]
            };
        });
    };

    onCancel = (line) => {
        const selectedLine = getSelectedLine(line);
        this.setState((state) => {
            const newLines = cloneDeep(state.lines);
            newLines[selectedLine].dates = calculateDates(this.props.techFormData, line);
            const lineFieldIds = state.lines[selectedLine].dates.map(d => d.id);
            return {
                lines: newLines,
                pendingFieldIds: state.pendingFieldIds.filter(id => !lineFieldIds.includes(id))
            };
        });
    };

    onDeleteDate = (line, id) => {
        const selectedLine = getSelectedLine(line);
        this.setState((state) => {
            const newLines = cloneDeep(state.lines);
            newLines[selectedLine].dates = newLines[selectedLine].dates.map((obj) => {
                if (obj.id !== id) return obj;
                return {
                    ...obj,
                    techFormPk: this.props.techFormData.techFormPk,
                    date: null,
                    line
                };
            });
            return {
                lines: newLines,
                pendingFieldIds: [...state.pendingFieldIds, id]
            };
        });
    };

    onRemoveDate = (line, id, dateIndex) => {
        const selectedLine = getSelectedLine(line);
        this.setState((state) => {
            const newLines = cloneDeep(state.lines);
            newLines[selectedLine].dates = newLines[selectedLine].dates.map((obj) => {
                if (obj.id !== id || !obj.isMultiDate) return obj;

                const updatedDates = [...(obj.dates || [])];
                updatedDates.splice(dateIndex, 1);
                return {
                    ...obj,
                    techFormPk: this.props.techFormData.techFormPk,
                    dates: updatedDates,
                    line
                };
            });
            return {
                lines: newLines,
                pendingFieldIds: [...state.pendingFieldIds, id]
            };
        });
    };

    onSave = (line) => {
        const selectedLine = getSelectedLine(line);
        const lineDates = this.state.lines[selectedLine].dates;

        // Validate dates
        const hasInvalidDates = lineDates.some((d) => {
            if (d.isMultiDate) {
                return d.dates && d.dates.some(date => date != null && isDateInvalid(date));
            }
            return d.date != null && isDateInvalid(d.date);
        });

        if (hasInvalidDates) {
            toastWarning('Wrong date, please check and update it');
            return;
        }

        // Check for duplicate dates in multi-date fields and show warning
        const hasDuplicates = lineDates.some((d) => {
            if (d.isMultiDate && d.dates && d.dates.length > 1) {
                const validDates = d.dates.filter(date => date != null);
                const uniqueDates = new Set(validDates);
                return uniqueDates.size !== validDates.length;
            }
            return false;
        });

        if (hasDuplicates) {
            toastWarning('Duplicate dates found. Only one copy of each date will be saved');
        }

        // Proceed with saving (duplicates will be automatically removed)
        const filteredFields = lineDates.filter(
            (obj) => this.state.pendingFieldIds.includes(obj.id) && obj.line === line
        );

        if (filteredFields.length === 0) {
            return;
        }

        // Prepare fields for API
        const fieldsToSave = [];
        const fieldsToClean = []; // Fields to clean in state after save

        filteredFields.forEach((field) => {
            if (field.isMultiDate) {
                // For multi-date fields, remove nulls and duplicates
                const validDates = (field.dates || []).filter(d => d != null);
                if (validDates.length > 0) {
                    // Remove duplicates using Set, then sort chronologically
                    const uniqueDates = [...new Set(validDates)];
                    const sortedDates = uniqueDates.sort((a, b) => a - b);
                    fieldsToSave.push({
                        ...field,
                        date: sortedDates.join(','), // Send as comma-separated string
                        dates: undefined,
                        isMultiDate: undefined
                    });
                    // Track fields that need cleaning (with unique dates only)
                    fieldsToClean.push({ id: field.id, validDates: sortedDates });
                } else {
                    // If no valid dates, send null to clear the field
                    fieldsToSave.push({
                        ...field,
                        date: null,
                        dates: undefined,
                        isMultiDate: undefined
                    });
                    fieldsToClean.push({ id: field.id, validDates: [] });
                }
            } else {
                // Single date fields — send null to clear when deleted
                fieldsToSave.push({
                    ...field,
                    date: field.date != null ? field.date : null,
                    isMultiDate: undefined
                });
            }
        });

        if (fieldsToSave.length > 0) {
            updateTechFormCharacters(fieldsToSave, (result) => {
                if (result.status === HTTP_CODE.OK) {
                    // Update state to remove null dates from multi-date fields
                    this.setState((state) => {
                        const newLines = cloneDeep(state.lines);
                        const selectedLine = getSelectedLine(line);

                        newLines[selectedLine].dates = newLines[selectedLine].dates.map((obj) => {
                            const cleanField = fieldsToClean.find(f => f.id === obj.id);
                            if (cleanField && obj.isMultiDate) {
                                return {
                                    ...obj,
                                    dates: cleanField.validDates // Keep only valid dates
                                };
                            }
                            return obj;
                        });

                        const idsSent = filteredFields.map((obj) => obj.id);
                        return {
                            lines: newLines,
                            pendingFieldIds: without(state.pendingFieldIds, idsSent)
                        };
                    });
                }
            });
        }
    };

    render() {
        const { cacType, techFormData } = this.props;
        const lines = getHarvestLines(cacType, techFormData.contractHarvest);

        return (
            <GreenRedTimeLine
                cacType={cacType}
                title={FLOWERING_DATES}
                lines={this.state.lines}
                lineCount={techFormData.line}
                lineLabels={lines}
                canBeEditable={canEditGrowing()}
                onDateEdited={this.onDateEdited}
                onAddDate={this.onAddDate}
                onRemoveDate={this.onRemoveDate}
                onDeleteDate={this.onDeleteDate}
                onCancel={this.onCancel}
                onSave={this.onSave}
            />
        );
    }
}

function isDateInvalid(date) {
    return isNaN(date) || date < 0;
}
